import {
  CosignFieldVerificationPolicyRequestSchema,
  CosignVerificationSettingsRequestSchema,
  ENDPOINTS,
  ProposeVerificationSettingsRequestSchema,
  SelfCertifyFieldVerificationPolicyRequestSchema,
  SelfCertifyVerificationSettingsRequestSchema,
  SetFieldVerificationPolicyRequestSchema,
  WithdrawVerificationSettingsRequestSchema,
  SetContractTypeAutoApprovalRequestSchema,
  type ContractTypeAutoApprovalResponse,
  type SetContractTypeAutoApprovalRequest,
  type SetContractTypeAutoApprovalResponse,
  type CosignFieldVerificationPolicyRequest,
  type CosignFieldVerificationPolicyResponse,
  type CosignVerificationSettingsRequest,
  type CosignVerificationSettingsResponse,
  type ProposeVerificationSettingsRequest,
  type ProposeVerificationSettingsResponse,
  type SelfCertifyFieldVerificationPolicyRequest,
  type SelfCertifyFieldVerificationPolicyResponse,
  type SelfCertifyVerificationSettingsRequest,
  type SelfCertifyVerificationSettingsResponse,
  type SetFieldVerificationPolicyRequest,
  type SetFieldVerificationPolicyResponse,
  type VerificationPolicyResponse,
  type VerificationSettingsResponse,
  type WithdrawVerificationSettingsRequest,
  type WithdrawVerificationSettingsResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';

/**
 * The Auto-approval surface's seven endpoints — CLM-108 (FR-31, FR-32, NFR-16,
 * CR-06; D-2.1-7, R-108-10 … R-108-12, R-108-24).
 *
 * Two reads and five writes, each gated on its own action server-side:
 *
 *  - `fetchVerificationPolicy` — the settings card and the per-field table
 *    (`admin:configure`). A practice-scoped Admin gets `fields: null` with
 *    `fields_withheld`, never a post-filtered figure (R-108-12).
 *  - `fetchVerificationSettings` — Legal's view: the same card, whether the
 *    caller proposed the pending change, and — for a Scope-All caller only —
 *    the impact and the opt-ins awaiting co-sign
 *    (`compliance:cosign_verification_settings`, Legal only).
 *  - `setFieldVerificationPolicy` — a new revision of one field's policy
 *    (`admin:configure` + Scope-All). The basis is captured server-side: the
 *    request carries no count, rate, UPN or object id, and the strict schema
 *    refuses one before anything is sent.
 *  - `cosignFieldVerificationPolicy` — Legal's co-sign or decline of an opt-in
 *    (R-108-24).
 *  - `proposeVerificationSettings`, `withdrawVerificationSettings`,
 *    `cosignVerificationSettings` — the threshold, sample rate and window, as a
 *    proposal an Admin makes and a **different** Legal holder makes effective
 *    (D-2.1-7). The co-sign carries `seen`, the values the co-signer was shown,
 *    so a change under them is `409 stale_view`, never an attestation to
 *    figures nobody read.
 *
 * Every request is validated `.strict()` before it is sent and every response
 * is parsed, not asserted: the response refinements pin the invariants the page
 * relies on (`fields` null exactly when withheld, one pending change, a decided
 * change naming a second person), so an off-contract body is a `ZodError` the
 * page reports, never a card showing a threshold nobody co-signed.
 *
 * NFR-06: no `scope` key is ever sent. Whether the caller is Scope-All is the
 * server's to know, and the page learns it only from `fields_withheld` and
 * from `impact: null`.
 */

const POLICY = ENDPOINTS['verification-policy'];
const SET_FIELD = ENDPOINTS['set-field-verification-policy'];
const COSIGN_FIELD = ENDPOINTS['cosign-field-verification-policy'];
const PROPOSE = ENDPOINTS['propose-verification-settings'];
const WITHDRAW = ENDPOINTS['withdraw-verification-settings'];
const SETTINGS = ENDPOINTS['verification-settings'];
const COSIGN_SETTINGS = ENDPOINTS['cosign-verification-settings'];
const SELF_CERTIFY_SETTINGS = ENDPOINTS['self-certify-verification-settings'];
const SELF_CERTIFY_FIELD = ENDPOINTS['self-certify-field-verification-policy'];
const TYPE_AUTO_APPROVAL = ENDPOINTS['contract-type-auto-approval'];
const SET_TYPE_AUTO_APPROVAL = ENDPOINTS['set-contract-type-auto-approval'];

function withSignal(signal: AbortSignal | undefined): { readonly signal?: AbortSignal } {
  return signal === undefined ? {} : { signal };
}

export async function fetchVerificationPolicy(signal?: AbortSignal): Promise<VerificationPolicyResponse> {
  const raw = await apiGet<unknown>(POLICY.path, withSignal(signal));
  return POLICY.response.parse(raw);
}

export async function fetchVerificationSettings(signal?: AbortSignal): Promise<VerificationSettingsResponse> {
  const raw = await apiGet<unknown>(SETTINGS.path, withSignal(signal));
  return SETTINGS.response.parse(raw);
}

export async function setFieldVerificationPolicy(
  request: SetFieldVerificationPolicyRequest,
  signal?: AbortSignal,
): Promise<SetFieldVerificationPolicyResponse> {
  const body = SetFieldVerificationPolicyRequestSchema.parse(request);
  const raw = await apiPost<unknown>(SET_FIELD.path, body, withSignal(signal));
  return SET_FIELD.response.parse(raw);
}

export async function cosignFieldVerificationPolicy(
  request: CosignFieldVerificationPolicyRequest,
  signal?: AbortSignal,
): Promise<CosignFieldVerificationPolicyResponse> {
  const body = CosignFieldVerificationPolicyRequestSchema.parse(request);
  const raw = await apiPost<unknown>(COSIGN_FIELD.path, body, withSignal(signal));
  return COSIGN_FIELD.response.parse(raw);
}

export async function proposeVerificationSettings(
  request: ProposeVerificationSettingsRequest,
  signal?: AbortSignal,
): Promise<ProposeVerificationSettingsResponse> {
  const body = ProposeVerificationSettingsRequestSchema.parse(request);
  const raw = await apiPost<unknown>(PROPOSE.path, body, withSignal(signal));
  return PROPOSE.response.parse(raw);
}

export async function withdrawVerificationSettings(
  request: WithdrawVerificationSettingsRequest,
  signal?: AbortSignal,
): Promise<WithdrawVerificationSettingsResponse> {
  const body = WithdrawVerificationSettingsRequestSchema.parse(request);
  const raw = await apiPost<unknown>(WITHDRAW.path, body, withSignal(signal));
  return WITHDRAW.response.parse(raw);
}

export async function cosignVerificationSettings(
  request: CosignVerificationSettingsRequest,
  signal?: AbortSignal,
): Promise<CosignVerificationSettingsResponse> {
  const body = CosignVerificationSettingsRequestSchema.parse(request);
  const raw = await apiPost<unknown>(COSIGN_SETTINGS.path, body, withSignal(signal));
  return COSIGN_SETTINGS.response.parse(raw);
}

/**
 * CLM-127, D-127-1 — the flag-gated self-certify path: one Scope-All Admin
 * proposes and decides in the same request, mirroring `proposeVerificationSettings`
 * / `setFieldVerificationPolicy` exactly (R-127-15). Offered only when the page's
 * last read carried `self_certify_enabled: true`; the server refuses `403
 * self_certify_disabled` otherwise, whatever this function is called with.
 */
export async function selfCertifyVerificationSettings(
  request: SelfCertifyVerificationSettingsRequest,
  signal?: AbortSignal,
): Promise<SelfCertifyVerificationSettingsResponse> {
  const body = SelfCertifyVerificationSettingsRequestSchema.parse(request);
  const raw = await apiPost<unknown>(SELF_CERTIFY_SETTINGS.path, body, withSignal(signal));
  return SELF_CERTIFY_SETTINGS.response.parse(raw);
}

export async function selfCertifyFieldVerificationPolicy(
  request: SelfCertifyFieldVerificationPolicyRequest,
  signal?: AbortSignal,
): Promise<SelfCertifyFieldVerificationPolicyResponse> {
  const body = SelfCertifyFieldVerificationPolicyRequestSchema.parse(request);
  const raw = await apiPost<unknown>(SELF_CERTIFY_FIELD.path, body, withSignal(signal));
  return SELF_CERTIFY_FIELD.response.parse(raw);
}

/**
 * CLM-121 (D-110-4, R-121-16) — the contract type's auto-approval switch: its
 * state and figures (`admin:configure`; the figures need Scope-All, otherwise
 * `withheld`), and switching it on or off (`admin:configure` + Scope-All, no
 * Legal co-sign). The request carries no count, UPN or object id; the strict
 * schema refuses one, and `on` with no note is the coded `note_required`.
 */
export async function fetchContractTypeAutoApproval(signal?: AbortSignal): Promise<ContractTypeAutoApprovalResponse> {
  const raw = await apiGet<unknown>(TYPE_AUTO_APPROVAL.path, withSignal(signal));
  return TYPE_AUTO_APPROVAL.response.parse(raw);
}

export async function setContractTypeAutoApproval(
  request: SetContractTypeAutoApprovalRequest,
  signal?: AbortSignal,
): Promise<SetContractTypeAutoApprovalResponse> {
  const body = SetContractTypeAutoApprovalRequestSchema.parse(request);
  const raw = await apiPost<unknown>(SET_TYPE_AUTO_APPROVAL.path, body, withSignal(signal));
  return SET_TYPE_AUTO_APPROVAL.response.parse(raw);
}
